import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Plus, X, Loader2 } from "lucide-react";
import { authApi, api, resolveImageUrl } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageFitControls from "@/components/admin/ImageFitControls";
import { scopeCss } from "@/lib/scopeCss";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EQUIP_CATS = ["Drivers", "Fairway Woods", "Hybrids", "Irons", "Wedges", "Putters"];
const SHOP_CATS = ["Bags", "Apparel", "Caps", "Gloves", "Accessories"];
const TAGS = ["None", "NEW", "SALE", "BESTSELLER"];
const PING_GRIP_CODES = ["Blue", "Red", "Aqua", "White", "Gold", "Orange"];
const GRIP_CODE_HEX = { Blue: "#1E5FBF", Red: "#D0322A", Aqua: "#3AB7C6", White: "#F5F5F5", Gold: "#D4A02C", Orange: "#E37820" };

const empty = {
  name: "", group: "shop", category: "Bags", price: "", old_price: "", tag: "None",
  short_desc: "", description: "", image: "", featured: false,
  image_fit: "cover", image_position: "center", variant_images: {},
  finishes: "", grip_color_codes: [],
  iron_clubs: "", iron_steel_per_club: "", iron_graphite_per_club: "",
  wedge_steel_per_piece: "", wedge_graphite_per_piece: "",
  spec_image: "", spec_image_fit: "cover", spec_image_position: "center",
  spec_html: "", spec_css: "",
  subcategory: "", subcategory_slug: "",
  iron_show_lie_length: true,
};

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileRef = useRef(null);

  const [form, setForm] = useState(empty);
  const [specs, setSpecs] = useState([{ k: "", v: "" }]);
  const [variants, setVariants] = useState([{ option: "", values: "" }]);
  const [gallery, setGallery] = useState([]);
  const [shafts, setShafts] = useState([]);
  const [grips, setGrips] = useState([]);
  const [loftGrinds, setLoftGrinds] = useState([]); // [{loft: "56°", grinds: "SS, WS, TS"}]
  const [techSlides, setTechSlides] = useState([]);
  const [techUploadingIdx, setTechUploadingIdx] = useState(-1);
  const techFileRefs = useRef([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    authApi.get(`/admin/products/${id}`).then(({ data }) => {
      setForm({
        name: data.name, group: data.group, category: data.category,
        price: String(data.price), old_price: data.old_price ? String(data.old_price) : "",
        tag: data.tag || "None", short_desc: data.short_desc || "",
        description: data.description || "", image: data.image || "", featured: data.featured,
        image_fit: data.image_fit || "cover", image_position: data.image_position || "center",
        variant_images: data.variant_images || {},
        finishes: (data.finishes || []).join(", "),
        grip_color_codes: data.grip_color_codes || [],
        iron_clubs: (data.iron_clubs || []).join(", "),
        iron_steel_per_club: data.iron_steel_per_club != null ? String(data.iron_steel_per_club) : "",
        iron_graphite_per_club: data.iron_graphite_per_club != null ? String(data.iron_graphite_per_club) : "",
        wedge_steel_per_piece: data.wedge_steel_per_piece != null ? String(data.wedge_steel_per_piece) : "",
        wedge_graphite_per_piece: data.wedge_graphite_per_piece != null ? String(data.wedge_graphite_per_piece) : "",
        spec_image: data.spec_image || "",
        spec_image_fit: data.spec_image_fit || "cover",
        spec_image_position: data.spec_image_position || "center",
        spec_html: data.spec_html || "",
        spec_css: data.spec_css || "",
        subcategory: data.subcategory || "",
        subcategory_slug: data.subcategory_slug || "",
        iron_show_lie_length: data.iron_show_lie_length !== false,
      });
      const s = Object.entries(data.specs || {}).map(([k, v]) => ({ k, v }));
      setSpecs(s.length ? s : [{ k: "", v: "" }]);
      const vr = Object.entries(data.variants || {}).map(([option, values]) => ({ option, values: values.join(", ") }));
      setVariants(vr.length ? vr : [{ option: "", values: "" }]);
      setGallery(data.gallery || []);
      setShafts((data.shafts || []).map((r) => ({
        name: r.name || "", flex: r.flex || "",
        flexes: Array.isArray(r.flexes) ? r.flexes.join(", ") : "",
        weight: r.weight || "", launch: r.launch || "",
      })));
      setGrips((data.grips || []).map((r) => ({ name: r.name || "", weight: r.weight || "", size: r.size || "" })));
      setLoftGrinds(Object.entries(data.loft_grinds || {}).map(([loft, grinds]) => ({ loft, grinds: (grinds || []).join(", ") })));
      setTechSlides((data.tech_slides || []).map((s) => ({ image: s.image || "", title: s.title || "", description: s.description || "" })));
      setLoading(false);
    }).catch(() => { toast.error("Could not load product"); navigate("/admin/products"); });
  }, [id, isEdit, navigate]);

  const uploadGallery = async (files) => {
    if (!files || !files.length) return;
    setGalleryUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setGallery((g) => [...g, resolveImageUrl(data.path)]);
      }
      toast.success("Gallery image(s) added");
    } catch { toast.error("Upload failed"); }
    finally { setGalleryUploading(false); }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadVariantImage = async (colour, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, variant_images: { ...(f.variant_images || {}), [colour]: resolveImageUrl(data.path) } }));
      toast.success(`Image set for ${colour}`);
    } catch { toast.error("Upload failed"); }
  };

  const removeVariantImage = (colour) => {
    setForm((f) => {
      const vi = { ...(f.variant_images || {}) };
      delete vi[colour];
      return { ...f, variant_images: vi };
    });
  };

  const onGroupChange = (g) => {
    setForm((f) => ({ ...f, group: g, category: g === "shop" ? SHOP_CATS[0] : EQUIP_CATS[0] }));
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("image", resolveImageUrl(data.path));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const specImageRef = useRef(null);
  const [specUploading, setSpecUploading] = useState(false);
  const uploadSpecImage = async (file) => {
    if (!file) return;
    setSpecUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("spec_image", resolveImageUrl(data.path));
    } catch { toast.error("Upload failed"); }
    finally { setSpecUploading(false); }
  };

  const [putterSubs, setPutterSubs] = useState([]);
  useEffect(() => {
    if (form.group === "equipment" && form.category === "Putters") {
      api.get("/site-settings").then(({ data }) => setPutterSubs(data.putter_subcategories || [])).catch(() => {});
    }
  }, [form.group, form.category]);

  const uploadTechImage = async (idx, file) => {
    if (!file) return;
    setTechUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTechSlides((arr) => arr.map((s, j) => j === idx ? { ...s, image: resolveImageUrl(data.path) } : s));
    } catch { toast.error("Upload failed"); }
    finally { setTechUploadingIdx(-1); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    if (!form.image) {
      toast.error("Please upload a product image");
      return;
    }
    const specsObj = {};
    specs.forEach((s) => { if (s.k.trim()) specsObj[s.k.trim()] = s.v.trim(); });
    const variantsObj = {};
    variants.forEach((v) => {
      if (v.option.trim() && v.values.trim()) {
        variantsObj[v.option.trim()] = v.values.split(",").map((x) => x.trim()).filter(Boolean);
      }
    });
    const galleryArr = gallery.length ? gallery : [form.image];
    const shaftsArr = shafts.filter((r) => r.name.trim()).map((r) => ({
      name: r.name.trim(),
      flex: r.flex ? r.flex.trim() : "",
      flexes: (r.flexes || "").split(",").map((x) => x.trim()).filter(Boolean),
      weight: r.weight ? r.weight.trim() : "",
      launch: r.launch ? r.launch.trim() : "",
    }));
    const gripsArr = grips.filter((r) => r.name.trim());
    const loftGrindsObj = {};
    loftGrinds.forEach((r) => {
      if (r.loft && r.loft.trim()) {
        loftGrindsObj[r.loft.trim()] = (r.grinds || "").split(",").map((x) => x.trim()).filter(Boolean);
      }
    });
    const finishesArr = (form.finishes || "").split(",").map((x) => x.trim()).filter(Boolean);
    const payload = {
      name: form.name.trim(), group: form.group, category: form.category,
      price: parseInt(form.price, 10), old_price: form.old_price ? parseInt(form.old_price, 10) : null,
      tag: form.tag === "None" ? null : form.tag, short_desc: form.short_desc,
      description: form.description, image: form.image, gallery: galleryArr,
      specs: specsObj, variants: variantsObj, shafts: shaftsArr, grips: gripsArr, featured: form.featured,
      image_fit: form.image_fit, image_position: form.image_position,
      variant_images: form.variant_images || {},
      loft_grinds: loftGrindsObj,
      finishes: finishesArr,
      grip_color_codes: form.grip_color_codes || [],
      iron_clubs: (form.iron_clubs || "").split(",").map((x) => x.trim()).filter(Boolean),
      iron_steel_per_club: form.iron_steel_per_club ? parseInt(form.iron_steel_per_club, 10) : null,
      iron_graphite_per_club: form.iron_graphite_per_club ? parseInt(form.iron_graphite_per_club, 10) : null,
      wedge_steel_per_piece: form.wedge_steel_per_piece ? parseInt(form.wedge_steel_per_piece, 10) : null,
      wedge_graphite_per_piece: form.wedge_graphite_per_piece ? parseInt(form.wedge_graphite_per_piece, 10) : null,
      spec_image: form.spec_image || "",
      spec_image_fit: form.spec_image_fit || "cover",
      spec_image_position: form.spec_image_position || "center",
      spec_html: form.spec_html || "",
      spec_css: form.spec_css || "",
      tech_slides: techSlides.filter((s) => (s.image || s.title || s.description)).map((s) => ({ image: s.image || "", title: s.title || "", description: s.description || "" })),
      subcategory: form.subcategory || null,
      subcategory_slug: form.subcategory_slug || null,
      iron_show_lie_length: form.iron_show_lie_length !== false,
    };
    setSaving(true);
    try {
      if (isEdit) await authApi.put(`/admin/products/${id}`, payload);
      else await authApi.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      navigate("/admin/products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cats = form.group === "shop" ? SHOP_CATS : EQUIP_CATS;
  const colourVariant = variants.find((v) => /colou?r/i.test(v.option || ""));
  const colourOptions = colourVariant ? colourVariant.values.split(",").map((x) => x.trim()).filter(Boolean) : [];

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl" data-testid="admin-product-form">
      <Link to="/admin/products" className="flex items-center gap-2 label-caps text-muted-foreground hover:text-[#0A0A0A] btn-transition mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to Products
      </Link>
      <h1 className="heading-2 mb-8">{isEdit ? "Edit Product" : "New Product"}</h1>

      <form onSubmit={save} className="grid lg:grid-cols-[300px_1fr] gap-8">
        {/* Image */}
        <div>
          <Label className="label-caps text-muted-foreground">Product Image</Label>
          <div className="mt-2 aspect-square bg-secondary border border-border flex items-center justify-center overflow-hidden">
            {form.image ? (
              <img src={form.image} alt="preview" className="w-full h-full" style={{ objectFit: form.image_fit, objectPosition: form.image_position }} data-testid="image-preview" />
            ) : (
              <span className="text-muted-foreground text-sm">No image</span>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} data-testid="image-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="w-full mt-3 border border-[#0A0A0A] py-3 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60" data-testid="upload-image-btn">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Upload Image</>}
          </button>

          {/* Image framing controls */}
          <div className="mt-4 space-y-3">
            <div>
              <Label className="label-caps text-muted-foreground">Image Fit</Label>
              <Select value={form.image_fit} onValueChange={(v) => setField("image_fit", v)}>
                <SelectTrigger className="mt-2 rounded-none" data-testid="field-image-fit"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="contain">Fit — show whole image (centered)</SelectItem>
                  <SelectItem value="cover">Fill — crop to fill frame</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Image Position</Label>
              <Select value={form.image_position} onValueChange={(v) => setField("image_position", v)}>
                <SelectTrigger className="mt-2 rounded-none" data-testid="field-image-position"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="bottom">Bottom</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Adjust how the image sits in its frame. The preview above updates live. Position mainly affects &ldquo;Fill&rdquo; mode.</p>
            </div>
          </div>
        </div>

        {/* Fields */}
        <div className="space-y-5">
          <div>
            <Label htmlFor="name" className="label-caps text-muted-foreground">Name</Label>
            <Input id="name" value={form.name} onChange={(e) => setField("name", e.target.value)} className="mt-2 rounded-none" data-testid="field-name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-caps text-muted-foreground">Type</Label>
              <Select value={form.group} onValueChange={onGroupChange}>
                <SelectTrigger className="mt-2 rounded-none" data-testid="field-group"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="shop">Pro Shop (sold online)</SelectItem>
                  <SelectItem value="equipment">Equipment (showcase only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger className="mt-2 rounded-none" data-testid="field-category"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="price" className="label-caps text-muted-foreground">Price (₹)</Label>
              <Input id="price" type="number" value={form.price} onChange={(e) => setField("price", e.target.value)} className="mt-2 rounded-none" data-testid="field-price" />
            </div>
            <div>
              <Label htmlFor="old_price" className="label-caps text-muted-foreground">Old Price</Label>
              <Input id="old_price" type="number" value={form.old_price} onChange={(e) => setField("old_price", e.target.value)} className="mt-2 rounded-none" data-testid="field-old-price" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Tag</Label>
              <Select value={form.tag} onValueChange={(v) => setField("tag", v)}>
                <SelectTrigger className="mt-2 rounded-none" data-testid="field-tag"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="short_desc" className="label-caps text-muted-foreground">Short Description</Label>
            <Input id="short_desc" value={form.short_desc} onChange={(e) => setField("short_desc", e.target.value)} className="mt-2 rounded-none" data-testid="field-short-desc" />
          </div>
          <div>
            <Label htmlFor="description" className="label-caps text-muted-foreground">Full Description</Label>
            <Textarea id="description" rows={4} value={form.description} onChange={(e) => setField("description", e.target.value)} className="mt-2 rounded-none" data-testid="field-description" />
          </div>

          {/* Specs */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="label-caps text-muted-foreground">Specifications</Label>
              <button type="button" onClick={() => setSpecs((s) => [...s, { k: "", v: "" }])} className="label-caps text-[#E31837] flex items-center gap-1" data-testid="add-spec-btn">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {specs.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Label (e.g. Material)" value={s.k} onChange={(e) => setSpecs((arr) => arr.map((x, j) => j === i ? { ...x, k: e.target.value } : x))} className="rounded-none" data-testid={`spec-key-${i}`} />
                  <Input placeholder="Value" value={s.v} onChange={(e) => setSpecs((arr) => arr.map((x, j) => j === i ? { ...x, v: e.target.value } : x))} className="rounded-none" data-testid={`spec-value-${i}`} />
                  <button type="button" onClick={() => setSpecs((arr) => arr.filter((_, j) => j !== i))} className="px-3 text-muted-foreground hover:text-[#E31837]" aria-label="Remove spec"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="label-caps text-muted-foreground">Variants / Options</Label>
              <button type="button" onClick={() => setVariants((s) => [...s, { option: "", values: "" }])} className="label-caps text-[#E31837] flex items-center gap-1" data-testid="add-variant-btn">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Enter comma-separated values (e.g. S, M, L, XL). Equipment options show as fitting choices; Pro Shop options are selectable by buyers.</p>
            <div className="mt-2 space-y-2">
              {variants.map((v, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Option (e.g. Size)" value={v.option} onChange={(e) => setVariants((arr) => arr.map((x, j) => j === i ? { ...x, option: e.target.value } : x))} className="rounded-none w-40" data-testid={`variant-option-${i}`} />
                  <Input placeholder="Values, comma separated" value={v.values} onChange={(e) => setVariants((arr) => arr.map((x, j) => j === i ? { ...x, values: e.target.value } : x))} className="rounded-none" data-testid={`variant-values-${i}`} />
                  <button type="button" onClick={() => setVariants((arr) => arr.filter((_, j) => j !== i))} className="px-3 text-muted-foreground hover:text-[#E31837]" aria-label="Remove variant"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Variant Colour Images (Pro Shop only — bags/apparel) */}
          {form.group === "shop" && colourOptions.length > 0 && (
            <div data-testid="variant-images-editor">
              <Label className="label-caps text-muted-foreground">Colour Variant Images</Label>
              <p className="text-xs text-muted-foreground mt-1">Upload a distinct image per colour. When a shopper picks a colour, that image will appear on the product page.</p>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                {colourOptions.map((colour) => {
                  const img = form.variant_images?.[colour];
                  const inputId = `variant-image-input-${colour.replace(/[^a-zA-Z0-9]/g, "")}`;
                  return (
                    <div key={colour} className="border border-border p-3" data-testid={`variant-image-${colour.replace(/[^a-zA-Z0-9]/g, "")}`}>
                      <div className="aspect-square bg-secondary overflow-hidden flex items-center justify-center">
                        {img ? <img src={img} alt={colour} className="w-full h-full object-cover" /> : <span className="text-xs text-muted-foreground">No image</span>}
                      </div>
                      <p className="mt-2 text-xs font-semibold uppercase tracking-wider">{colour}</p>
                      <input id={inputId} type="file" accept="image/*" hidden onChange={(e) => uploadVariantImage(colour, e.target.files[0])} />
                      <div className="mt-2 flex gap-2">
                        <label htmlFor={inputId} className="flex-1 border border-[#0A0A0A] py-1.5 text-xs label-caps flex items-center justify-center gap-1 btn-transition hover:bg-[#0A0A0A] hover:text-white cursor-pointer" data-testid={`variant-image-upload-${colour.replace(/[^a-zA-Z0-9]/g, "")}`}>
                          <Upload className="h-3 w-3" /> {img ? "Replace" : "Upload"}
                        </label>
                        {img && (
                          <button type="button" onClick={() => removeVariantImage(colour)} className="px-2 border border-border text-muted-foreground hover:text-[#E31837]" data-testid={`variant-image-remove-${colour.replace(/[^a-zA-Z0-9]/g, "")}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gallery images */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="label-caps text-muted-foreground">Gallery Images (carousel on detail page)</Label>
              <button type="button" onClick={() => galleryRef.current?.click()} disabled={galleryUploading} className="label-caps text-[#E31837] flex items-center gap-1 disabled:opacity-60" data-testid="add-gallery-btn">
                {galleryUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add Images
              </button>
            </div>
            <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadGallery(e.target.files)} data-testid="gallery-input" />
            <p className="text-xs text-muted-foreground mt-1">First image shows first. If empty, the main image is used.</p>
            <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {gallery.map((g, i) => (
                <div key={i} className="relative aspect-square bg-secondary border border-border overflow-hidden group" data-testid={`gallery-item-${i}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setGallery((arr) => arr.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-[#0A0A0A] text-white p-1 opacity-0 group-hover:opacity-100 btn-transition" aria-label="Remove image" data-testid={`gallery-remove-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Shafts (equipment only) */}
          {form.group === "equipment" && (
            <div data-testid="shafts-editor">
              <div className="flex items-center justify-between">
                <Label className="label-caps text-muted-foreground">Shaft Options</Label>
                <button type="button" onClick={() => setShafts((s) => [...s, { name: "", flex: "", flexes: "", weight: "", launch: "" }])} className="label-caps text-[#E31837] flex items-center gap-1" data-testid="add-shaft-btn">
                  <Plus className="h-3.5 w-3.5" /> Add Shaft
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                <b>Flex Options</b> (comma-separated) drives the dependent Flex picker on the product page. e.g. <code>Regular, Stiff, X-Stiff</code>. <b>Flex Summary</b> is the label shown in the specs table.
              </p>
              <div className="mt-2 space-y-2">
                {shafts.length === 0 && <p className="text-xs text-muted-foreground">No shaft options yet.</p>}
                {shafts.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_130px_1fr_90px_100px_32px] gap-2">
                    <Input placeholder="Shaft name" value={r.name} onChange={(e) => setShafts((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="rounded-none" data-testid={`shaft-name-${i}`} />
                    <Input placeholder="Flex summary" value={r.flex} onChange={(e) => setShafts((arr) => arr.map((x, j) => j === i ? { ...x, flex: e.target.value } : x))} className="rounded-none" data-testid={`shaft-flex-${i}`} />
                    <Input placeholder="Flex options (R, S, X)" value={r.flexes || ""} onChange={(e) => setShafts((arr) => arr.map((x, j) => j === i ? { ...x, flexes: e.target.value } : x))} className="rounded-none" data-testid={`shaft-flexes-${i}`} />
                    <Input placeholder="Weight" value={r.weight} onChange={(e) => setShafts((arr) => arr.map((x, j) => j === i ? { ...x, weight: e.target.value } : x))} className="rounded-none" data-testid={`shaft-weight-${i}`} />
                    <Input placeholder="Launch" value={r.launch} onChange={(e) => setShafts((arr) => arr.map((x, j) => j === i ? { ...x, launch: e.target.value } : x))} className="rounded-none" data-testid={`shaft-launch-${i}`} />
                    <button type="button" onClick={() => setShafts((arr) => arr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-[#E31837]" aria-label="Remove shaft"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loft × Grind matrix (wedges) */}
          {form.group === "equipment" && form.category === "Wedges" && (
            <div data-testid="loft-grinds-editor">
              <div className="flex items-center justify-between">
                <Label className="label-caps text-muted-foreground">Loft × Grind Matrix</Label>
                <button type="button" onClick={() => setLoftGrinds((s) => [...s, { loft: "", grinds: "" }])} className="label-caps text-[#E31837] flex items-center gap-1" data-testid="add-loft-btn">
                  <Plus className="h-3.5 w-3.5" /> Add Loft
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Each loft can carry its own set of grinds (comma-separated). e.g. <code>56°</code> → <code>SS, WS, TS, EF</code>. When this is set, the storefront hides the plain &ldquo;Loft&rdquo; variant and shows a dependent Loft → Grind picker.</p>
              <div className="mt-2 space-y-2">
                {loftGrinds.length === 0 && <p className="text-xs text-muted-foreground">No loft-grind rows yet.</p>}
                {loftGrinds.map((r, i) => (
                  <div key={i} className="grid grid-cols-[110px_1fr_32px] gap-2">
                    <Input placeholder="Loft (46°)" value={r.loft} onChange={(e) => setLoftGrinds((arr) => arr.map((x, j) => j === i ? { ...x, loft: e.target.value } : x))} className="rounded-none" data-testid={`loft-input-${i}`} />
                    <Input placeholder="Grinds (SS, WS, TS)" value={r.grinds} onChange={(e) => setLoftGrinds((arr) => arr.map((x, j) => j === i ? { ...x, grinds: e.target.value } : x))} className="rounded-none" data-testid={`grinds-input-${i}`} />
                    <button type="button" onClick={() => setLoftGrinds((arr) => arr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-[#E31837]" aria-label="Remove loft"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Finishes (e.g. Chrome / Midnight) */}
          {form.group === "equipment" && (
            <div data-testid="finishes-editor">
              <Label className="label-caps text-muted-foreground">Finishes</Label>
              <Input value={form.finishes} onChange={(e) => setField("finishes", e.target.value)} placeholder="Chrome, Midnight" className="mt-1 rounded-none" data-testid="field-finishes" />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated. Leave empty to hide the Finish selector on the product page.</p>
            </div>
          )}

          {/* Grip Colour Codes (drivers/woods/hybrids/wedges) */}
          {form.group === "equipment" && (
            <div data-testid="grip-color-codes-editor">
              <Label className="label-caps text-muted-foreground">PING Grip Colour Codes</Label>
              <p className="text-xs text-muted-foreground mt-1">Toggle which colour-code grip sizes are offered. Typically enabled on drivers, fairway woods, hybrids and wedges.</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {PING_GRIP_CODES.map((c) => {
                  const active = (form.grip_color_codes || []).includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm((f) => ({
                        ...f,
                        grip_color_codes: active
                          ? (f.grip_color_codes || []).filter((x) => x !== c)
                          : [...(f.grip_color_codes || []), c],
                      }))}
                      title={c}
                      className={`h-10 w-10 rounded-full border-2 btn-transition flex items-center justify-center ${active ? "border-[#0A0A0A] scale-110" : "border-border hover:border-[#0A0A0A]"}`}
                      style={{ backgroundColor: GRIP_CODE_HEX[c] }}
                      data-testid={`grip-code-toggle-${c.toLowerCase()}`}
                    >
                      {active && <span className="text-[10px] font-bold" style={{ color: c === "White" ? "#0A0A0A" : "#fff" }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Iron Set Builder (irons only) */}
          {form.group === "equipment" && form.category === "Irons" && (
            <div data-testid="iron-set-builder-editor" className="border border-border p-4 space-y-3">
              <Label className="label-caps">Iron Set Builder</Label>
              <p className="text-xs text-muted-foreground -mt-1">Set per-club prices; the storefront multiplies by clubs selected. Leave a price blank to hide that material. Leave <b>Clubs</b> empty to fall back to the 8-slot default (4, 5, 6, 7, 8, 9, PW, UW).</p>
              <div>
                <Label className="label-caps text-muted-foreground">Available Clubs</Label>
                <Input value={form.iron_clubs} onChange={(e) => setField("iron_clubs", e.target.value)} placeholder="4, 5, 6, 7, 8, 9, PW, UW" className="mt-1 rounded-none" data-testid="field-iron-clubs" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps text-muted-foreground">Steel ₹ / club</Label>
                  <Input type="number" value={form.iron_steel_per_club} onChange={(e) => setField("iron_steel_per_club", e.target.value)} placeholder="e.g. 15999" className="mt-1 rounded-none" data-testid="field-iron-steel-per-club" />
                </div>
                <div>
                  <Label className="label-caps text-muted-foreground">Graphite ₹ / club</Label>
                  <Input type="number" value={form.iron_graphite_per_club} onChange={(e) => setField("iron_graphite_per_club", e.target.value)} placeholder="e.g. 17999" className="mt-1 rounded-none" data-testid="field-iron-graphite-per-club" />
                </div>
              </div>
              <label className="flex items-center gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.iron_show_lie_length !== false}
                  onChange={(e) => setField("iron_show_lie_length", e.target.checked)}
                  className="h-4 w-4 accent-[#E31837]"
                  data-testid="field-iron-show-lie-length"
                />
                <span className="text-sm">Show <b>Lie Angle</b> &amp; <b>Length</b> pickers on product page</span>
              </label>
              <p className="text-xs text-muted-foreground -mt-1">Uncheck for chippers / utility clubs (e.g. ChipR) that don&apos;t need per-club lie fitting.</p>
            </div>
          )}

          {/* Key Specifications HTML/CSS editor — available for ALL equipment products (drivers/woods/hybrids/irons/wedges/putters) */}
          {form.group === "equipment" && (
            <div data-testid="spec-html-editor" className="border border-border p-4 space-y-3">
              <Label className="label-caps">Key Specifications — HTML &amp; CSS</Label>
              <p className="text-xs text-muted-foreground -mt-1">Author the spec table exactly how you want it. Your CSS is auto-scoped to this section only (it can&apos;t leak to the rest of the site). Leave both empty to hide the &ldquo;Key Specifications&rdquo; section on the product page.</p>
              <div className="grid lg:grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps text-muted-foreground">HTML</Label>
                  <Textarea
                    value={form.spec_html}
                    onChange={(e) => setField("spec_html", e.target.value)}
                    placeholder={`<table class="specs">\n  <thead><tr><th>Club</th><th>Loft</th><th>Lie</th><th>Bounce</th></tr></thead>\n  <tbody>\n    <tr><td>4-iron</td><td>19.0°</td><td>60.5°</td><td>6.0°</td></tr>\n  </tbody>\n</table>`}
                    rows={12}
                    className="mt-1 rounded-none font-mono text-xs"
                    spellCheck={false}
                    data-testid="field-spec-html"
                  />
                </div>
                <div>
                  <Label className="label-caps text-muted-foreground">CSS (scoped)</Label>
                  <Textarea
                    value={form.spec_css}
                    onChange={(e) => setField("spec_css", e.target.value)}
                    placeholder={`.specs { width: 100%; border-collapse: collapse; font-size: 14px; }\n.specs th, .specs td { border-bottom: 1px solid #e5e5e5; padding: 12px 8px; text-align: left; }\n.specs th { text-transform: uppercase; letter-spacing: 0.06em; font-size: 11px; color: #666; }`}
                    rows={12}
                    className="mt-1 rounded-none font-mono text-xs"
                    spellCheck={false}
                    data-testid="field-spec-css"
                  />
                </div>
              </div>
              {(form.spec_html || form.spec_css) && (
                <div>
                  <Label className="label-caps text-muted-foreground">Live Preview</Label>
                  <div id="spec-preview-scope" className="mt-1 border border-border p-4 bg-white" data-testid="spec-html-preview">
                    <style dangerouslySetInnerHTML={{ __html: scopeCss(form.spec_css || "", "spec-preview-scope") }} />
                    <div dangerouslySetInnerHTML={{ __html: form.spec_html || "<p style='color:#999;font-size:13px'>Preview appears here</p>" }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Technology carousel (equipment only) — black-background feature cards above Key Specs */}
          {form.group === "equipment" && (
            <div data-testid="tech-slides-editor" className="border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="label-caps">Technology Carousel</Label>
                  <p className="text-xs text-muted-foreground mt-1">Add feature callouts (e.g. Carbonfly Wrap, CG Shifter, T9S+ Face). Each slide has an image, a title and a short description. Rendered on a black background above Key Specifications, 3 slides at a time on desktop.</p>
                </div>
                <button type="button" onClick={() => setTechSlides((a) => [...a, { image: "", title: "", description: "" }])} className="label-caps text-[#E31837] flex items-center gap-1" data-testid="add-tech-slide-btn">
                  <Plus className="h-3.5 w-3.5" /> Add Slide
                </button>
              </div>
              <div className="space-y-3">
                {techSlides.length === 0 && <p className="text-xs text-muted-foreground">No technology slides yet.</p>}
                {techSlides.map((s, idx) => (
                  <div key={idx} className="grid md:grid-cols-[180px_1fr_32px] gap-3 bg-secondary/40 p-3" data-testid={`tech-slide-${idx}`}>
                    <div>
                      <div className="aspect-video bg-[#0A0A0A] overflow-hidden flex items-center justify-center">
                        {s.image ? <img src={s.image} alt="" className="w-full h-full object-contain" /> : <span className="text-white/40 text-xs">No image</span>}
                      </div>
                      <input ref={(el) => (techFileRefs.current[idx] = el)} type="file" accept="image/*" hidden onChange={(e) => uploadTechImage(idx, e.target.files[0])} data-testid={`tech-slide-image-input-${idx}`} />
                      <button type="button" onClick={() => techFileRefs.current[idx]?.click()} disabled={techUploadingIdx === idx} className="w-full mt-2 border border-[#0A0A0A] py-1.5 text-xs label-caps flex items-center justify-center gap-1 btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60" data-testid={`tech-slide-upload-${idx}`}>
                        {techUploadingIdx === idx ? <><Loader2 className="h-3 w-3 animate-spin" /> Uploading…</> : <><Upload className="h-3 w-3" /> Image</>}
                      </button>
                    </div>
                    <div className="space-y-2">
                      <Input placeholder="Title (e.g. Dual Carbonfly Wrap)" value={s.title} onChange={(e) => setTechSlides((arr) => arr.map((x, j) => j === idx ? { ...x, title: e.target.value } : x))} className="rounded-none uppercase tracking-wider" data-testid={`tech-slide-title-${idx}`} />
                      <Textarea placeholder="Short description shown below the title (2–3 lines)." value={s.description} onChange={(e) => setTechSlides((arr) => arr.map((x, j) => j === idx ? { ...x, description: e.target.value } : x))} rows={3} className="rounded-none" data-testid={`tech-slide-description-${idx}`} />
                    </div>
                    <button type="button" onClick={() => setTechSlides((arr) => arr.filter((_, j) => j !== idx))} className="text-muted-foreground hover:text-[#E31837] self-start" aria-label="Remove slide" data-testid={`tech-slide-remove-${idx}`}><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Putter family (subcategory) */}
          {form.group === "equipment" && form.category === "Putters" && putterSubs.length > 0 && (
            <div data-testid="putter-subcategory-editor">
              <Label className="label-caps text-muted-foreground">Putter Family</Label>
              <Select
                value={form.subcategory_slug || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") { setField("subcategory_slug", ""); setField("subcategory", ""); return; }
                  const s = putterSubs.find((x) => x.slug === v);
                  setField("subcategory_slug", v);
                  setField("subcategory", s ? s.name : "");
                }}
              >
                <SelectTrigger className="mt-1 rounded-none" data-testid="field-subcategory-slug"><SelectValue placeholder="Select family" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {putterSubs.map((s) => (
                    <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Groups this putter under its family page at <code>/equipment/putters/{form.subcategory_slug || "<slug>"}</code>. Manage families in Admin → Putter Families.</p>
            </div>
          )}

          {/* Wedge Set Builder (wedges only) */}
          {form.group === "equipment" && form.category === "Wedges" && (
            <div data-testid="wedge-set-builder-editor" className="border border-border p-4 space-y-3">
              <Label className="label-caps">Wedge Set Builder</Label>
              <p className="text-xs text-muted-foreground -mt-1">Set per-piece prices; the storefront multiplies by the number of wedge lofts the customer picks. Leave a price blank to hide that material. Lofts and grinds come from the Loft × Grind Matrix above.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps text-muted-foreground">Steel ₹ / piece</Label>
                  <Input type="number" value={form.wedge_steel_per_piece} onChange={(e) => setField("wedge_steel_per_piece", e.target.value)} placeholder="e.g. 20999" className="mt-1 rounded-none" data-testid="field-wedge-steel-per-piece" />
                </div>
                <div>
                  <Label className="label-caps text-muted-foreground">Graphite ₹ / piece</Label>
                  <Input type="number" value={form.wedge_graphite_per_piece} onChange={(e) => setField("wedge_graphite_per_piece", e.target.value)} placeholder="e.g. 24100" className="mt-1 rounded-none" data-testid="field-wedge-graphite-per-piece" />
                </div>
              </div>
            </div>
          )}

          {/* Grips (equipment only) */}
          {form.group === "equipment" && (
            <div data-testid="grips-editor">
              <div className="flex items-center justify-between">
                <Label className="label-caps text-muted-foreground">Grip Options</Label>
                <button type="button" onClick={() => setGrips((s) => [...s, { name: "", weight: "", size: "" }])} className="label-caps text-[#E31837] flex items-center gap-1" data-testid="add-grip-btn">
                  <Plus className="h-3.5 w-3.5" /> Add Grip
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {grips.length === 0 && <p className="text-xs text-muted-foreground">No grip options yet.</p>}
                {grips.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_120px_32px] gap-2">
                    <Input placeholder="Grip name" value={r.name} onChange={(e) => setGrips((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="rounded-none" data-testid={`grip-name-${i}`} />
                    <Input placeholder="Weight" value={r.weight} onChange={(e) => setGrips((arr) => arr.map((x, j) => j === i ? { ...x, weight: e.target.value } : x))} className="rounded-none" data-testid={`grip-weight-${i}`} />
                    <Input placeholder="Size" value={r.size} onChange={(e) => setGrips((arr) => arr.map((x, j) => j === i ? { ...x, size: e.target.value } : x))} className="rounded-none" data-testid={`grip-size-${i}`} />
                    <button type="button" onClick={() => setGrips((arr) => arr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-[#E31837]" aria-label="Remove grip"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}


          <div className="flex items-center justify-between border border-border p-4">
            <div>
              <Label className="label-caps">Featured</Label>
              <p className="text-xs text-muted-foreground">Show on homepage featured sections</p>
            </div>
            <Switch checked={form.featured} onCheckedChange={(v) => setField("featured", v)} data-testid="field-featured" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="bg-[#E31837] text-white px-8 py-4 label-caps btn-transition hover:bg-[#b3122c] active:scale-95 disabled:opacity-60" data-testid="save-product-btn">
              {saving ? "Saving…" : isEdit ? "Update Product" : "Create Product"}
            </button>
            <Link to="/admin/products" className="border border-[#0A0A0A] px-8 py-4 label-caps btn-transition hover:bg-[#0A0A0A] hover:text-white flex items-center">Cancel</Link>
          </div>
        </div>
      </form>
    </div>
  );
}
